
import React from 'react';
import { Download, Chrome, Info, ExternalLink, Folder } from 'lucide-react';

export const ExtensionDownload: React.FC = () => {
  const downloadFile = (filename: string, content: string) => {
    const element = document.createElement('a');
    const file = new Blob([content], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = filename;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const manifest = {
    "manifest_version": 3,
    "name": "MyESALON Helper",
    "version": "1.0",
    "description": "Auto-fill helper for MyESALON Order Automation.",
    "permissions": ["storage", "tabs"],
    "content_scripts": [
      {
        "matches": ["<all_urls>"],
        "js": ["content.js"]
      }
    ],
    "background": {
      "service_worker": "background.js"
    }
  };

  const contentJs = `// Listen for messages from the MyESALON web app
window.addEventListener("message", (event) => {
  if (event.data && event.data.type === 'MYESALON_EXTENSION_TRIGGER') {
    chrome.runtime.sendMessage({
      type: 'START_AUTOFILL',
      payload: event.data.payload
    });
  }
});

// If we are on the target signup page, check if we have data to fill
if (window.location.href.includes("myesalon.com/signup")) {
  chrome.storage.local.get(['autofillData'], (result) => {
    if (result.autofillData) {
      const data = result.autofillData;
      
      function setVal(id, val) {
        const el = document.getElementById(id);
        if (!el || !val) return;
        el.value = val;
        el.dispatchEvent(new Event('input', { bubbles: true }));
        el.dispatchEvent(new Event('change', { bubbles: true }));
        el.style.backgroundColor = '#dcfce7';
      }

      function setDropdown(id, val) {
        const el = document.getElementById(id);
        if (!el || !val) return;
        const options = Array.from(el.options);
        const match = options.find(o => 
            o.value.toLowerCase() === val.toLowerCase() || 
            o.text.toLowerCase().includes(val.toLowerCase())
        );
        if (match) {
            el.value = match.value;
            el.dispatchEvent(new Event('change', { bubbles: true }));
            el.style.backgroundColor = '#dcfce7';
        }
      }

      setTimeout(() => {
        setVal('tb_firstname', data.ownerInfo.firstName);
        setVal('tb_lastname', data.ownerInfo.lastName);
        setVal('tb_contactphone', data.ownerInfo.phone);
        setVal('tb_email', data.ownerInfo.email);
        setVal('tb_businessname', data.businessInfo.name);
        setVal('tb_businessphone', data.businessInfo.phone);
        setVal('tb_address', data.businessInfo.address);
        setVal('tb_city', data.businessInfo.city);
        setVal('tb_zipcode', data.businessInfo.zip);
        setDropdown('dropdown_state', data.businessInfo.state);

        // Plan Selection mapping
        const planToSelect = data.package === 'Elite' || data.package === 'Supreme' 
            ? 'Elite Marketing Plan' 
            : data.package;
        if (planToSelect) {
            setDropdown('dropdown_plan', planToSelect);
        }

        setVal('tb_user', data.generatedCreds.username);
        setVal('tb_password', data.generatedCreds.password);
        if (data.businessInfo.website) setVal('tb_domain', data.businessInfo.website);
        
        chrome.storage.local.remove(['autofillData']);
        console.log('MyESALON: Form Auto-Filled!');
      }, 1000);
    }
  });
}`;

  const backgroundJs = `chrome.runtime.onMessage.addListener((request, sender, sendResponse) => {
  if (request.type === 'START_AUTOFILL') {
    chrome.storage.local.set({ autofillData: request.payload }, () => {
      chrome.tabs.create({ url: 'https://myesalon.com/signup' });
    });
  }
});`;

  return (
    <div className="max-w-2xl mx-auto mb-12 glass-panel rounded-3xl border-white/50 shadow-lg overflow-hidden text-left bg-white/70">
      <div className="p-6 bg-white/50 border-b border-white/40 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="bg-gradient-to-br from-blue-500 to-indigo-600 p-2 rounded-xl shadow-lg shadow-blue-500/20">
            <Chrome className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-bold text-slate-800">Chrome Extension Setup</h3>
            <p className="text-xs text-slate-500 font-medium">Enable "One-Click" Auto-Fill functionality</p>
          </div>
        </div>
        <div className="flex items-center gap-1 text-[10px] bg-blue-100 text-blue-700 px-2 py-1 rounded font-bold uppercase shadow-sm">
          <Info className="w-3 h-3" /> Required for Step 3
        </div>
      </div>

      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="space-y-4">
            <h4 className="font-bold text-sm text-slate-700 flex items-center gap-2">
              <Download className="w-4 h-4 text-blue-500" /> 1. Download Files
            </h4>
            <p className="text-xs text-slate-500 leading-relaxed font-medium">Download these 3 files into a new folder on your computer named <code className="bg-slate-100 px-1 rounded border border-slate-200">MyESALONExtension</code>.</p>
            <div className="flex flex-col gap-2">
              <DownloadButton label="manifest.json" onClick={() => downloadFile('manifest.json', JSON.stringify(manifest, null, 2))} />
              <DownloadButton label="content.js" onClick={() => downloadFile('content.js', contentJs)} />
              <DownloadButton label="background.js" onClick={() => downloadFile('background.js', backgroundJs)} />
            </div>
          </div>

          <div className="space-y-4">
            <h4 className="font-bold text-sm text-slate-700 flex items-center gap-2">
              <ExternalLink className="w-4 h-4 text-blue-500" /> 2. Install in Chrome
            </h4>
            <ul className="text-xs text-slate-500 space-y-3 font-medium">
              <li className="flex gap-2">
                <span className="bg-slate-200 text-slate-700 w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-[10px]">1</span>
                <span>Open <code className="bg-slate-100 px-1 rounded border border-slate-200">chrome://extensions</code> in a new tab.</span>
              </li>
              <li className="flex gap-2">
                <span className="bg-slate-200 text-slate-700 w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-[10px]">2</span>
                <span>Toggle <b>Developer mode</b> (top right) to ON.</span>
              </li>
              <li className="flex gap-2">
                <span className="bg-slate-200 text-slate-700 w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-[10px]">3</span>
                <span>Click <b>Load unpacked</b> and select your folder.</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

const DownloadButton = ({ label, onClick }: { label: string, onClick: () => void }) => (
  <button 
    onClick={onClick}
    className="flex items-center justify-between px-3 py-2.5 bg-white/60 border border-white/50 rounded-xl hover:bg-white/80 hover:border-blue-300 transition-all text-left group shadow-sm"
  >
    <div className="flex items-center gap-2">
      <Folder className="w-4 h-4 text-slate-400 group-hover:text-blue-500 transition-colors" />
      <span className="text-xs font-mono font-bold text-slate-600">{label}</span>
    </div>
    <Download className="w-3 h-3 text-slate-300 group-hover:text-blue-500 transition-colors" />
  </button>
);
